import { TOOL_NAMES } from 'js/src/agent/toolNames';
import React from 'react';
import { z } from 'zod';

import {
    type CustomRendererComponentProps,
    CustomRendererMode,
    type EnhancedToolResult,
    type ToolExecutionContext,
} from '../../types';
import { Tool } from '../Tool';

import { openAnimatedExplainer } from 'js/src/components/animated-explainer-videos/utils/openAnimatedExplainer';
import AnimationUpsellBanner from 'js/src/components/common/ui/AnimationUpsellBanner';

export const suggestAnimatedExplainerSchema = z.object({});
export type SuggestAnimatedExplainerArgs = z.infer<typeof suggestAnimatedExplainerSchema>;

const CTA_SOURCE = 'sketch routing';

/**
 * Custom renderer that surfaces the GenX (animated explainer) CTA banner inline in the
 * Sketch conversation when the agent detects the user is describing animation content.
 */
const AnimatedExplainerToolRenderer: React.FC<CustomRendererComponentProps> = () => {
    React.useEffect(() => {
        analytics.track('GenX CTA Seen', { source: CTA_SOURCE });
    }, []);

    const handleCtaClick = React.useCallback(() => {
        analytics.track('GenX CTA Clicked', { source: CTA_SOURCE });
        openAnimatedExplainer({});
    }, []);

    return (
        <div className="my-l5-8">
            <AnimationUpsellBanner
                // header="Try an animated explainer"
                // description="It sounds like you want an animation — our animated explainer is built for exactly this kind of video."
                // ctaText="Try now"
                onCtaClick={handleCtaClick}
            />
        </div>
    );
};

/**
 * Tool the agent calls when the user's request is for an animated explainer (animation,
 * illustration, cartoon, 2D, 3D) rather than a stock-footage video. It renders the GenX
 * CTA banner so the user can move that idea into the animated explainer feature.
 */
export class SuggestAnimatedExplainerTool extends Tool<typeof suggestAnimatedExplainerSchema> {
    protected override readonly schema = suggestAnimatedExplainerSchema;
    protected override readonly toolName = TOOL_NAMES.suggestAnimatedExplainer;
    protected override readonly toolDescription =
        'Show the animated explainer (GenX) call-to-action banner for a drawn/rendered ' +
        'animation request. Calling this does not create a video.';

    public override customRenderer = AnimatedExplainerToolRenderer;
    public override customRendererMode = CustomRendererMode.FULL;

    public override getToolDisplayName(): string {
        return 'Suggesting animated explainer';
    }

    public get statusIcon(): string {
        return 'lightbulb';
    }

    async execute(
        _args: SuggestAnimatedExplainerArgs,
        _context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        return {
            result:
                'The animated explainer CTA banner has been shown to the user. They can click it to ' +
                'try the animated explainer feature. Do not repeat this — briefly let them know the ' +
                'animated explainer is better suited for this kind of animation request, and offer ' +
                'to make a regular video instead if they prefer.',
            isSuccess: true,
        };
    }
}
